/**
 * 檔案用途區塊
 * @module PaymentHandler
 * @purpose 統一處理 WalletConnect 支付流程，依鏈別分流至 EVM 或 Bitcoin 支付實作，並整合連線狀態、錯誤處理與 i18n 顯示。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */

import React, { useRef } from 'react';
import { useAppKitAccount } from '@reown/appkit/react';
import { useSendTransaction, useWriteContract, useChainId } from 'wagmi';
import { parseEther, parseUnits, isAddress, type Address } from 'viem';
import { parseNamespace } from '@modules/walletconnect/config/token-network-adapter-mapping';
import {
  getPaymentHandlerType,
  checkNetwork,
  handleBitcoinPayment,
  isUserRejectedError,
  createPaymentError,
  isValidWalletAddressForChain,
  type PaymentParams,
} from '@modules/walletconnect/helpers/payment-handlers';
import { useThirdPartyProvider } from '@shared/models';
import { useWalletConnectI18n } from '@modules/walletconnect/hooks/useWalletConnectI18n';
import type { WalletConnectI18nKey } from '@locale/keys';

interface PaymentHandlerProps {
  paymentParams: PaymentParams;
  onSuccess: (txHash: string) => void;
  onError: (error: Error) => void;
  /** 為 true 時進入後自動發起交易，不顯示「發起支付」按鈕 */
  autoSend?: boolean;
  /** 使用者在錢包內拒絕交易時回呼 */
  onUserCancel?: () => void;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description PaymentHandler 元件入口，負責判斷目前支付鏈別並選擇對應子處理器。
 * @purpose 在 UI 層封裝鏈別分流、連線前置檢查與不支援鏈提示。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
const PaymentHandler: React.FC<PaymentHandlerProps> = ({
  paymentParams,
  onSuccess,
  onError,
  autoSend = false,
  onUserCancel,
}) => {
  const { isConnected, caipAddress } = useAppKitAccount();
  const chainId = useChainId();
  const { payload } = useThirdPartyProvider();
  const { t } = useWalletConnectI18n(payload.lang);

  // 檢查是否已連接
  if (!isConnected || !caipAddress) {
    return <div>{t('payment.connect.description')}</div>;
  }

  // 根據 chainId 判斷支付處理類型
  const handlerType = getPaymentHandlerType(paymentParams.chainId);

  // EVM 鏈支付
  if (handlerType === 'evm') {
    return (
      <EVMPaymentHandler
        paymentParams={paymentParams}
        currentChainId={chainId}
        onSuccess={onSuccess}
        onError={onError}
        autoSend={autoSend}
        onUserCancel={onUserCancel}
        t={t}
      />
    );
  }

  // Bitcoin 支付
  if (handlerType === 'bitcoin') {
    return (
      <BitcoinPaymentHandler
        paymentParams={paymentParams}
        onSuccess={onSuccess}
        onError={onError}
        autoSend={autoSend}
        t={t}
      />
    );
  }

  // 不支援的鏈
  const namespace = parseNamespace(paymentParams.chainId);
  return (
    <div>
      {t('payment.unsupportedChain')}
      {namespace}
    </div>
  );
};

/**
 * EVM 鏈支付處理器
 */
interface EVMPaymentHandlerProps {
  paymentParams: PaymentParams;
  currentChainId: number;
  onSuccess: (txHash: string) => void;
  onError: (error: Error) => void;
  autoSend?: boolean;
  onUserCancel?: () => void;
  t: (key: WalletConnectI18nKey) => string;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description EVMPaymentHandler 負責 EVM 原生幣與 ERC20 轉帳流程，並監聽交易結果與錯誤狀態。
 * @purpose 透過 wagmi hooks 執行交易送出、錯誤分類（含使用者拒絕）與自動送單控制。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
const EVMPaymentHandler: React.FC<EVMPaymentHandlerProps> = ({
  paymentParams,
  currentChainId,
  onSuccess,
  onError,
  autoSend = false,
  onUserCancel,
  t,
}) => {
  const { sendTransaction, isPending: isSending, data: txHash, error: txError } = useSendTransaction();
  const { writeContract, isPending: isWriting, data: contractTxHash, error: contractError } = useWriteContract();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const autoSendTriggeredRef = React.useRef(false);

  // 使用 useRef 穩定 callback 引用，避免 useEffect 依賴項問題
  const onSuccessRef = React.useRef(onSuccess);
  const onErrorRef = React.useRef(onError);
  const onUserCancelRef = React.useRef(onUserCancel);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onUserCancelRef.current = onUserCancel;

  // 監聽交易結果
  React.useEffect(() => {
    if (txHash) {
      onSuccessRef.current(txHash);
    }
  }, [txHash]);

  React.useEffect(() => {
    if (contractTxHash) {
      onSuccessRef.current(contractTxHash);
    }
  }, [contractTxHash]);

  // 監聽錯誤
  React.useEffect(() => {
    if (txError) {
      const error = txError instanceof Error ? txError : new Error(String(txError));

      if (isUserRejectedError(error)) {
        setIsProcessing(false);
        onUserCancelRef.current?.();
        return;
      }

      onErrorRef.current(error);
      setIsProcessing(false);
    }
  }, [txError]);

  React.useEffect(() => {
    if (contractError) {
      const error = contractError instanceof Error ? contractError : new Error(String(contractError));

      if (isUserRejectedError(error)) {
        setIsProcessing(false);
        onUserCancelRef.current?.();
        return;
      }

      onErrorRef.current(error);
      setIsProcessing(false);
    }
  }, [contractError]);

  // 使用 useRef 穩定函數引用，減少不必要的重新建立
  const sendTransactionRef = React.useRef(sendTransaction);
  const writeContractRef = React.useRef(writeContract);
  sendTransactionRef.current = sendTransaction;
  writeContractRef.current = writeContract;

  const handleConfirm = React.useCallback(async () => {
    if (isProcessing || isSending || isWriting) {
      return;
    }

    setIsProcessing(true);

    try {
      // 最後一道防線：檢查網路是否正確（只檢查，不自動切換）
      const isNetworkCorrect = checkNetwork(paymentParams.chainId, currentChainId);
      if (!isNetworkCorrect) {
        throw createPaymentError('networkMismatch');
      }

      // 運行時驗證 walletAddress 格式（依 chain 使用 EVM / Bitcoin 格式）
      if (!isValidWalletAddressForChain(paymentParams.walletAddress, paymentParams.chainId)) {
        throw createPaymentError('invalidWalletAddress');
      }

      // 判斷是否為原生幣或 ERC20 token
      const isNativeToken = !paymentParams.isToken || !paymentParams.contractAddress;

      if (isNativeToken) {
        // 原生幣支付（ETH, ETC 等）- 使用 sendTransaction，會自動觸發錢包確認彈窗
        // EVM 流程下 walletAddress 已通過 isValidWalletAddressForChain(eip155) 驗證
        sendTransactionRef.current({
          to: paymentParams.walletAddress as Address,
          value: parseEther(paymentParams.amount),
        });
      } else {
        // ERC20 token 支付 - 使用 writeContract，會自動觸發錢包確認彈窗
        if (!paymentParams.contractAddress) {
          throw createPaymentError('tokenAddressRequired');
        }

        // 運行時驗證合約地址格式（避免類型斷言）
        if (!isAddress(paymentParams.contractAddress)) {
          throw createPaymentError('invalidContractAddress');
        }

        const tokenDecimals = paymentParams.decimals ?? 18;
        const value = parseUnits(paymentParams.amount, tokenDecimals);

        writeContractRef.current({
          address: paymentParams.contractAddress,
          abi: [
            {
              constant: false,
              inputs: [
                { name: '_to', type: 'address' },
                { name: '_value', type: 'uint256' },
              ],
              name: 'transfer',
              outputs: [{ name: '', type: 'bool' }],
              type: 'function',
            },
          ],
          functionName: 'transfer',
          args: [paymentParams.walletAddress as Address, value],
        });
      }
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      onErrorRef.current(error);
      setIsProcessing(false);
    }
  }, [paymentParams, currentChainId, isProcessing, isSending, isWriting]);

  // autoSend：進入後自動觸發一次
  React.useEffect(() => {
    if (!autoSend || autoSendTriggeredRef.current || isSending || isWriting || isProcessing) {
      return;
    }
    autoSendTriggeredRef.current = true;
    void handleConfirm();
  }, [autoSend, handleConfirm, isSending, isWriting, isProcessing]);

  const isPending = isSending || isWriting;

  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      {!autoSend && (
        <button
          onClick={handleConfirm}
          disabled={isPending || isProcessing}
          style={{
            padding: '12px 24px',
            backgroundColor: isPending || isProcessing ? '#6c757d' : '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: isPending || isProcessing ? 'not-allowed' : 'pointer',
            fontSize: '16px',
          }}>
          {isPending || isProcessing ? t('payment.status.processing') : t('payment.sendButton')}
        </button>
      )}
      {(isPending || isProcessing) && (
        <div style={{ marginTop: '16px', fontSize: '14px', color: '#666' }}>{t('payment.status.waitingWallet')}</div>
      )}
    </div>
  );
};

/**
 * Bitcoin 支付處理器
 */
interface BitcoinPaymentHandlerProps {
  paymentParams: PaymentParams;
  onSuccess: (txHash: string) => void;
  onError: (error: Error) => void;
  autoSend?: boolean;
  t: (key: WalletConnectI18nKey) => string;
}

/**
 * 宣告內容用途說明與單號關聯
 * @description BitcoinPaymentHandler 於生命週期中執行 Bitcoin 支付並回傳交易結果。
 * @purpose 管理 Bitcoin 付款非同步流程與處理中狀態呈現。
 * @external https://innotech.atlassian.net/browse/IN-111889
 */
const BitcoinPaymentHandler: React.FC<BitcoinPaymentHandlerProps> = ({
  paymentParams,
  onSuccess,
  onError,
  autoSend = false,
  t,
}) => {
  const [isProcessing, setIsProcessing] = React.useState(false);

  React.useEffect(() => {
    const processPayment = async () => {
      if (isProcessing) {
        return;
      }

      setIsProcessing(true);

      try {
        const txHash = await handleBitcoinPayment(paymentParams);
        onSuccess(txHash);
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        onError(error);
      } finally {
        setIsProcessing(false);
      }
    };

    void processPayment();
  }, [paymentParams, onSuccess, onError, isProcessing]);

  if (isProcessing) {
    return (
      <div style={{ textAlign: 'center', padding: '20px', marginTop: '8px', color: '#666' }}>
        {t('payment.status.processing')}
      </div>
    );
  }

  return null;
};

export default PaymentHandler;

/**
 * === llm 分析紀錄區 ===
 * @llm-review-submitted-at 2026-06-14T07:15:53.098Z
 * @llm-review-model gpt-5.3-codex
 * @llm-review-note 已完成純註解重構：新增檔案頂部「檔案用途區塊」、為三個主要宣告補上「宣告內容用途說明與單號關聯」JSDoc（含 IN-111889 Jira 全網址）、並在檔尾加入「llm 分析紀錄區」。程式邏輯與執行內容未變更。
 */
